import { httpClient } from "@/lib/http";
import { DRIVERS_ENDPOINTS, ADMIN_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/lib/http";
import type { ApprovalSubmission } from "@/features/approvals/types";
import type {
    DriversQueryParams,
    DriversResponse,
    DriverDetails,
    DriverRidesResponse,
    DriverTripsResponse,
    DriverWalletResponse,
    RoadWorthinessReviewAction,
} from "../types";

// ============================================================================
// Request Types
// ============================================================================

type UpdateUserStatusPayload = {
    action: "activate" | "reactivate" | "deactivate" | "suspend" | "ban";
    reason?: string;
};

type ReviewRoadWorthinessPayload = {
    action: RoadWorthinessReviewAction;
    reason?: string;
};

// ============================================================================
// Helper Functions
// ============================================================================

const buildQueryString = (params: DriversQueryParams): string => {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) queryParams.append("page", params.page.toString());
    if (params.size !== undefined) queryParams.append("size", params.size.toString());
    if (params.status) queryParams.append("status", params.status);
    if (params.search) queryParams.append("search", params.search);
    if (params.emailVerified !== undefined && params.emailVerified !== "")
        queryParams.append("emailVerified", params.emailVerified.toString());
    if (params.phoneVerified !== undefined && params.phoneVerified !== "")
        queryParams.append("phoneVerified", params.phoneVerified.toString());
    if (params.ninVerified !== undefined && params.ninVerified !== "")
        queryParams.append("ninVerified", params.ninVerified.toString());
    if (params.createdFrom) queryParams.append("createdFrom", params.createdFrom);
    if (params.createdTo) queryParams.append("createdTo", params.createdTo);

    return queryParams.toString();
};

// ============================================================================
// API Functions
// ============================================================================

export const getDrivers = async (params: DriversQueryParams = {}) => {
    const queryString = buildQueryString(params);
    const url = queryString ? `${DRIVERS_ENDPOINTS.LIST}?${queryString}` : DRIVERS_ENDPOINTS.LIST;
    const { data } = await httpClient.get<ApiResponse<DriversResponse>>(url);
    return data;
};

export const getDriverDetails = async (driverId: string) => {
    const { data } = await httpClient.get<ApiResponse<DriverDetails>>(
        DRIVERS_ENDPOINTS.DETAILS(driverId)
    );
    return data;
};

export const updateUserStatus = async (userId: string, payload: UpdateUserStatusPayload) => {
    const { data } = await httpClient.put<ApiResponse<ApprovalSubmission>>(
        ADMIN_ENDPOINTS.USER_STATUS(userId),
        payload
    );
    return data;
};

export const activateDriver = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "activate", reason });
};

export const deactivateDriver = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "deactivate", reason });
};

export const reactivateDriver = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "reactivate", reason });
};

export const suspendDriver = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "suspend", reason });
};

export const banDriver = async (userId: string, reason?: string) => {
    return updateUserStatus(userId, { action: "ban", reason });
};

export const deleteDriver = async (userId: string, reason?: string) => {
    const { data } = await httpClient.delete<ApiResponse<ApprovalSubmission>>(
        DRIVERS_ENDPOINTS.DELETE(userId),
        { data: reason ? { reason } : undefined }
    );
    return data;
};

export const getDriverRides = async (driverId: string, page = 0, size = 10) => {
    const { data } = await httpClient.get<ApiResponse<DriverRidesResponse>>(
        `${DRIVERS_ENDPOINTS.RIDES(driverId)}?page=${page}&size=${size}`
    );
    return data;
};

export const getDriverTrips = async (driverId: string, page = 0, size = 10) => {
    const { data } = await httpClient.get<ApiResponse<DriverTripsResponse>>(
        `${DRIVERS_ENDPOINTS.TRIPS(driverId)}?page=${page}&size=${size}`
    );
    return data;
};

export const getDriverWallet = async (driverId: string, page = 0, size = 10) => {
    const { data } = await httpClient.get<ApiResponse<DriverWalletResponse>>(
        `${DRIVERS_ENDPOINTS.WALLET(driverId)}?page=${page}&size=${size}`
    );
    return data;
};

export const reviewDriverRoadWorthiness = async (
    driverId: string,
    payload: ReviewRoadWorthinessPayload
) => {
    const { data } = await httpClient.put<ApiResponse<{ message: string }>>(
        DRIVERS_ENDPOINTS.ROAD_WORTHINESS_REVIEW(driverId),
        payload
    );
    return data;
};
